import { useEffect, useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { default as Grid } from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';

import { enqueueSnackbar } from 'notistack';

import useInvoicePdf from 'hooks-ts/useInvoicePdf';
import { sendInvoicePdfEmail } from 'utils-ts/sales-and-invoicing';
import handleError from 'utils-ts/handle-error';

import { TipTapEditor } from 'ui-component/TipTapEditor';
import { type InvoicePdfData, type CreditNote, type Invoice } from '@a1/shared';
import useCreditNotePdf from 'hooks-ts/useCreditNotePdf';
import { useParams } from 'react-router';
import { INVOICE_EMAIL_MESSAGE_DEFAULT } from 'constants-ts/environmentalVariables';
import EmailChipInput from '../input/EmailChipInput'; // 1. Import the new component

interface InvoicePdfDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  billingDocument?: Invoice | CreditNote | null;
}

const isInvoice = (doc: Invoice | CreditNote): doc is Invoice => 'invoiceNumber' in doc;

// To deprecate
// use SendInvoicePdfDialog instead
const SendBillingDocumentPdfDialog = ({ open, setOpen, billingDocument }: InvoicePdfDialogProps) => {
  const { invoiceId } = useParams();

  const { getInvoicePdfData, generateInvoicePdf } = useInvoicePdf();
  const { generateCreditNotePdf, getCreditNotePdfData } = useCreditNotePdf();

  const billingDocumentId = billingDocument?.id;

  const [subject, setSubject] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [html, setHtml] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);

  const [billingDocumentPdfData, setBillingDocumentPdfData] = useState<InvoicePdfData | null>(null);
  const [loadingBillingDocumentPdfData, setLoadingBillingDocumentPdfData] = useState(true);

  const bankDetails = billingDocumentPdfData?.bankDetails;

  const documentDetails = useMemo(() => {
    if (!billingDocument) {
      return undefined;
    }

    if (isInvoice(billingDocument)) {
      return {
        documentNumber: billingDocument.invoiceNumber,
        name: 'Invoice'
      };
    }
    return {
      documentNumber: billingDocument.creditNoteNumber,
      name: 'Credit Note'
    };
  }, [billingDocument]);

  useEffect(() => {
    if (!billingDocument) {
      return;
    }

    if (isInvoice(billingDocument)) {
      setHtml(`${INVOICE_EMAIL_MESSAGE_DEFAULT}`);
    } else {
      setHtml(`Please find attached a copy of your Credit Note ${billingDocument.creditNoteNumber} for Invoice ${invoiceId}.`);
    }
  }, [billingDocument, invoiceId]);

  useEffect(() => {
    const getPdfData = async () => {
      if (!billingDocument) {
        return;
      }

      try {
        setLoadingBillingDocumentPdfData(true);

        if (isInvoice(billingDocument)) {
          const invoicePdfDetails = await getInvoicePdfData({ invoiceId: billingDocumentId });
          if (invoicePdfDetails) {
            setBillingDocumentPdfData(invoicePdfDetails);
          }
        } else {
          const creditNotePdfDetails = await getCreditNotePdfData({ creditNoteId: billingDocumentId });
          if (creditNotePdfDetails) {
            setBillingDocumentPdfData(creditNotePdfDetails);
          }
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingBillingDocumentPdfData(false);
      }
    };

    if (open && billingDocumentId) {
      getPdfData();
    } else {
      setLoadingBillingDocumentPdfData(false);
    }
  }, [setBillingDocumentPdfData, getInvoicePdfData, billingDocumentId, open, billingDocument, getCreditNotePdfData]);

  const disableSend = emails.length < 1 || !html.trim() || !billingDocument || !billingDocumentPdfData?.blob;

  const handleClose = () => {
    setOpen(false);
    setEmails([]);
    setSubject('');
    setHtml('');
  };

  useEffect(() => {
    if (documentDetails?.documentNumber && billingDocument) {
      if (isInvoice(billingDocument)) {
        setSubject(`${documentDetails.documentNumber}${bankDetails?.companyName ? ` from ${bankDetails.companyName}` : ''}`);
      } else {
        setSubject(`A Credit Note has been issued for Invoice ${invoiceId}.`);
      }
    }
  }, [bankDetails?.companyName, billingDocument, documentDetails, invoiceId]);

  const handleSend = async () => {
    if (!billingDocument) {
      return;
    }
    if (!billingDocumentPdfData?.blob) {
      throw new Error('Invoice PDF blob is not available.');
    }

    setSending(true);
    try {
      // To deprecate, use PdfSender instead
      await sendInvoicePdfEmail({
        emails,
        subject,
        html,
        pdfBlob: billingDocumentPdfData.blob,
        invoiceNumber: documentDetails?.documentNumber ?? undefined,
        onSuccess: (msg) =>
          enqueueSnackbar({
            variant: 'success',
            message: msg,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          }),
        onError: (msg) =>
          enqueueSnackbar({
            variant: 'error',
            message: msg,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          })
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h3">Send PDF {documentDetails?.documentNumber && `for ${documentDetails.documentNumber}`}</Typography>
        </DialogTitle>

        {loadingBillingDocumentPdfData ? (
          <LinearProgress />
        ) : (
          <>
            <DialogContent>
              {billingDocument ? (
                <Grid container pt={2} spacing={2}>
                  <Grid size={12}>
                    <EmailChipInput emails={emails} onChange={setEmails} disabled={sending} />
                  </Grid>

                  {/* subject */}
                  <Grid size={12}>
                    <TextField
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                      }}
                      fullWidth
                      label="Subject"
                      disabled={sending}
                    />
                  </Grid>

                  <Grid size={12}>
                    <TipTapEditor data={html} onChange={setHtml} disabled={sending} />
                  </Grid>

                  {billingDocumentPdfData?.url && (
                    <Grid size={12}>
                      <Button
                        startIcon={<DownloadIcon />}
                        fullWidth
                        onClick={() => {
                          if (isInvoice(billingDocument)) {
                            generateInvoicePdf({
                              invoiceId: billingDocumentId,
                              filename: `${documentDetails?.documentNumber ?? documentDetails?.name}.pdf`
                            });
                          } else {
                            generateCreditNotePdf({
                              creditNoteId: billingDocumentId,
                              filename: `${documentDetails?.documentNumber ?? documentDetails?.name}.pdf`
                            });
                          }
                        }}
                      >
                        {documentDetails?.documentNumber ?? documentDetails?.name}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography>Invoice could not be loaded.</Typography>
              )}
            </DialogContent>

            <DialogActions>
              <Button color="error" variant="text" onClick={handleClose} disabled={sending}>
                Close
              </Button>
              <Button loading={sending} disabled={disableSend} onClick={handleSend}>
                Send
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default SendBillingDocumentPdfDialog;
